<template>
  <div class="select-currency">
    <p class="select-currency__subtitle">Choose the currency you want to fund your wallet with</p>
    
    <div class="select-currency__list">
      <div
        v-for="curr in currencyOptions"
        :key="curr.code"
        class="select-currency__option"
        :class="{ 'select-currency__option--selected': currency === curr.code }"
        @click="selectCurrency(curr.code)"
      >
        <div class="select-currency__option-left">
          <CurrencyFlag :currency="curr.code" class="select-currency__flag" />
          <div class="select-currency__option-info">
            <span class="select-currency__option-name">{{ curr.name }}</span>
            <span class="select-currency__option-code">{{ curr.code }}</span>
          </div>
        </div>
        <div v-if="currency === curr.code" class="select-currency__check">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M3 8.5L6.5 12L13 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
      </div>
    </div>

    <button
      class="select-currency__btn"
      :disabled="!currency || loading"
      @click="storeData"
    >
      <span v-if="loading" class="select-currency__spinner"></span>
      <span v-else>Continue</span>
    </button>
  </div>
</template>

<script setup>
  import CurrencyFlag from "@/components/wallet/CurrencyFlag.vue";
  import { getItem } from "@/utils/storage.js";
  import { useStore } from "vuex";
  import { computed, onMounted, ref } from "vue";
  import { useFundingProvider } from "@/composables/fundingProvider";

  const { getCurrencies, formatCurrencies } = useFundingProvider();
  const emits = defineEmits(["storeData"]);
  const store = useStore();

  const currency = ref("");
  const userDetails = ref({});
  
  const currencyOptions = ref([
    { code: "NGN", name: "Nigerian Naira" },
    { code: "USD", name: "US Dollar" },
    { code: "EUR", name: "Euro" },
    { code: "GBP", name: "British Pound" },
    { code: "GHS", name: "Ghanaian Cedi" },
    { code: "KES", name: "Kenyan Shilling" },
    { code: "RWF", name: "Rwandan Franc" },
    { code: "UGX", name: "Ugandan Shilling" },
    { code: "TZS", name: "Tanzanian Shilling" },
    { code: "ZAR", name: "South African Rand" },
  ]);

  const loading = computed(() => store.state.smallLoading);

  const selectCurrency = (code) => {
    currency.value = code;
  };

  const storeData = async () => {
    if (!currency.value) return;
    store.dispatch("organisationModule/setCurrency", currency.value);
    const exchangeQuery = { from: "USD", to: currency.value };
    const response = await store.dispatch(
      "organisationModule/getExchangeRate",
      exchangeQuery
    );
    if (response) {
      emits("storeData", "selectCurrencyForm");
    }
  };

  onMounted(() => {
    userDetails.value = getItem("org_details");
    getCurrencies();
  });
</script>

<style lang="scss" scoped>
// Design System Variables
$text-primary: #0F172A;
$text-secondary: #475569;
$text-muted: #64748B;
$bg-card: #FFFFFF;
$bg-subtle: #F1F5F9;
$border-color: #E2E8F0;
$accent-primary: #6366F1;
$accent-light: #EEF2FF;
$btn-primary: #001240;
$btn-primary-hover: #06206B;

.select-currency {
  &__subtitle {
    font-size: 13px;
    color: $text-muted;
    margin-bottom: 24px;
    line-height: 1.5;
  }

  &__list {
    border: 1px solid $border-color;
    border-radius: 10px;
    overflow: hidden;
    margin-bottom: 24px;
    max-height: 400px;
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: $border-color;
      border-radius: 4px;
    }
  }

  &__option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    cursor: pointer;
    transition: background 0.15s ease;
    border-bottom: 1px solid $border-color;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: $bg-subtle;
    }

    &--selected {
      background: $accent-light;

      &:hover {
        background: $accent-light;
      }
    }
  }

  &__option-left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__flag {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    overflow: hidden;
    flex-shrink: 0;
  }

  &__option-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__option-name {
    font-size: 14px;
    font-weight: 500;
    color: $text-primary;
  }

  &__option-code {
    font-size: 12px;
    color: $text-muted;
  }

  &__check {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: $accent-primary;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
  }

  &__btn {
    width: 100%;
    height: 48px;
    background: $btn-primary;
    border: none;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 500;
    color: white;
    cursor: pointer;
    transition: background 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover:not(:disabled) {
      background: $btn-primary-hover;
    }

    &:disabled {
      background: $border-color;
      color: $text-muted;
      cursor: not-allowed;
    }
  }

  &__spinner {
    width: 20px;
    height: 20px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
